import { SearchApiSdk } from '../../api/SearchApiSdk';
import { SearchCatalog } from '../domain/entities/SearchCatalog';
import { SearchFiltersCatalog } from '../domain/entities/SearchFiltersCatalog';
import { SearchPaginationCatalog } from '../domain/entities/SearchPaginationCatalog';
import { SearchTypeaheadCatalog } from '../domain/entities/SearchTypeaheadCatalog';
import { SearchContentCatalog } from '../domain/entities/SearchContentCatalog';
import { SearchArticleCatalog } from '../domain/entities/SearchArticleCatalog';
import { IItem, ItemTypes, IProdObjListItem } from '../interfaces/ISearchInterfaces';
import { DISPLAY_SKU_IDS } from '../constants/Search';
import { observable, action, computed } from 'mobx';

export interface ISearchQuery {
    searchTerm: string;
    wildCard?: boolean;
    searchDisplayFormat: string;
    enableContentSearch?: boolean;
    articleSearch?: boolean;
}

export interface ISearchRepository {
    searchApiSdk: SearchApiSdk;
}

export class SearchRepository {
    private searchApiSdk: SearchApiSdk;
    @observable private searchCatalog: SearchCatalog;
    @observable private searchFiltersCatalog: SearchFiltersCatalog;
    @observable private searchPaginationCatalog: SearchPaginationCatalog;
    @observable public searchTypeaheadCatalog: SearchTypeaheadCatalog;
    @observable public searchContentCatalog: SearchContentCatalog;
    @observable public searchArticleCatalog: SearchArticleCatalog;
    @observable public items: IItem[] = [];
    @observable public searchResultsLoaded: boolean = false;
    @observable public searchTypeaheadLoaded: boolean = false;

    constructor(props: ISearchRepository) {
        this.searchApiSdk = props.searchApiSdk;
        this.searchCatalog = new SearchCatalog();
        this.searchFiltersCatalog = new SearchFiltersCatalog();
        this.searchPaginationCatalog = new SearchPaginationCatalog();
        this.searchTypeaheadCatalog = new SearchTypeaheadCatalog();
        this.searchContentCatalog = new SearchContentCatalog();
        this.searchArticleCatalog = new SearchArticleCatalog();
    }

    @computed
    public get isTypeaheadProductsLoaded() {
        return this.searchResultsLoaded && this.searchTypeaheadLoaded;
    }

    @action
    public setSearchResultsLoaded = (isLoaded: boolean) => {
        this.searchResultsLoaded = isLoaded;
    };

    public getSearchResults = async (search: ISearchQuery) => {
        this.searchResultsLoaded = false;

        try {
            if (search.enableContentSearch && search.articleSearch) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const searchArticleResults: any = await this.searchApiSdk.getSearchResults(search);
                search.articleSearch = false;
                this.searchArticleCatalog.update(searchArticleResults);
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const searchResults: any = await this.searchApiSdk.getSearchResults(search);

            if (searchResults.hasOwnProperty('url')) {
                this.searchCatalog.endecaRedirectUrl = searchResults.url;
            } else {
                this.searchCatalog.endecaRedirectUrl = null;
                this.updatefromJson(searchResults);
                this.searchDisplayResults(search.searchDisplayFormat);

                if (
                    this.searchResults.prodObjListItems.length ||
                    this.searchCatalog.skuIds.length
                ) {
                    this.searchResultsLoaded = true;
                }

                if (
                    (!search.wildCard &&
                        this.searchResults.prodObjListItems.length === 0 &&
                        this.articles.articles.length === 0) ||
                    (this.searchCatalog.skuIds.length === 0 && this.articles.articles.length === 0)
                ) {
                    search.wildCard = true;
                    const wildCardSearchResults = await this.searchApiSdk.getSearchResults(search);
                    this.updatefromJson(wildCardSearchResults);
                    this.searchDisplayResults(search.searchDisplayFormat);
                    this.searchResultsLoaded = true;
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    public getTypeaheadResults = async (search: ISearchQuery) => {
        this.searchTypeaheadLoaded = false;
        try {
            const searchTypeaheadResults = await this.searchApiSdk.getTypeaheadResults(search);
            this.searchTypeaheadCatalog.update(searchTypeaheadResults);
            this.searchTypeaheadLoaded = true;
        } catch (error) {
            console.error(error);
        }
    };

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public updatefromJson = (searchResults: any) => {
        this.searchCatalog.update(searchResults);
        this.searchContentCatalog.update(searchResults);
        this.searchFiltersCatalog.update(searchResults);
        this.searchPaginationCatalog.searchPagination(searchResults);
    };

    public searchDisplayResults(searchDisplayFormat: string) {
        const productList = this.searchCatalog.prodObjListItems;
        const skuList = this.searchCatalog.skuIds;

        if (searchDisplayFormat === DISPLAY_SKU_IDS) {
            this.items.push(
                ...skuList.map(
                    (skuId: string): IItem => ({
                        type: ItemTypes.sku,
                        params: {
                            id: skuId
                        }
                    })
                )
            );
        } else {
            this.items.push(
                ...productList.map(
                    (itemProp: IProdObjListItem): IItem => ({
                        type: ItemTypes.product,
                        params: {
                            id: itemProp.productId,
                            defaultSkuId: itemProp.defaultSkuId
                        }
                    })
                )
            );
        }

        return this.items;
    }

    @action
    public clearRepositoryItems() {
        this.items = [];
    }

    @computed
    public get searchResults() {
        return this.searchCatalog;
    }

    @computed
    public get filters() {
        return this.searchFiltersCatalog;
    }

    @computed
    public get pagination() {
        return this.searchPaginationCatalog;
    }

    @computed
    public get typeahead() {
        return this.searchTypeaheadCatalog;
    }

    @computed
    public get content() {
        return this.searchContentCatalog;
    }

    @computed
    public get articles() {
        return this.searchArticleCatalog;
    }
}
